import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { dateToSqlDate } from '../../helpers/dateFuncs';
import fetchData from '../../helpers/fetchData';
import { formatNumber } from '../../helpers/numberUtils';
import { exportToExcel } from '../../helpers/exportFuncs';
import { FilterContext, IFilterState } from '../../contexts/FilterContext';
import { getComparator, Order, sortedRowInformation } from '../../helpers/sortingsFuncs';
import LoadingSpinner from '../LoadingSpinner';
import { visuallyHidden } from '@mui/utils';

interface HeaderMappingItem {
  name: string;
  type: string;
  digits: number;
  attach: string;
}

const detailsHeaderMapping: Record<string, HeaderMappingItem> = {
  snapshot_date: {
    name: 'Snapshot Datum',
    type: 'string',
    digits: 0,
    attach: '',
  },
  datum: {
    name: 'Lead Datum',
    type: 'string',
    digits: 0,
    attach: '',
  },
  e_affiliate_id: {
    name: 'Affiliate ID',
    type: 'string',
    digits: 0,
    attach: '',
  },
  column_name: {
    name: 'Spalte',
    type: 'string',
    digits: 0,
    attach: '',
  },
  old_value: {
    name: 'Alter Wert',
    type: 'number',
    digits: 2,
    attach: '',
  },
  new_value: {
    name: 'Neuer Wert',
    type: 'number',
    digits: 2,
    attach: '',
  },
  change_amount: {
    name: 'Änderung (€)',
    type: 'number',
    digits: 2,
    attach: '€',
  },
  change_percentage: {
    name: 'Änderung (%)',
    type: 'number',
    digits: 2,
    attach: '%',
  },
  network: {
    name: 'Netzwerk',
    type: 'string',
    digits: 0,
    attach: '',
  },
  theme: {
    name: 'Thema',
    type: 'string',
    digits: 0,
    attach: '',
  },
  planungsbereich: {
    name: 'Planungsbereich',
    type: 'string',
    digits: 0,
    attach: '',
  },
};

type SnapshotDecreaseData = {
  snapshot_date: string;
  datum: string;
  e_affiliate_id: string;
  anmeldungen: number;
  column_name: string;
  old_value: string;
  new_value: string;
  change_amount: number;
  change_percentage: number;
  network: string;
  theme: string;
  planungsbereich: string;
  newsletter?: string;
  [key: string]: any;
};

type Data = Record<DataKey, string>;
type DataKey = keyof SnapshotDecreaseData;

interface SnapshotDecreasesTableProps {
  startDate: Date | null;
  endDate: Date | null;
  network?: string[];
  theme?: string[];
  planungsbereich?: string[];
  column?: string;
  minChange?: number;
  leadStartDate?: Date | null;
  leadEndDate?: Date | null;
  onRowClick?: (row: SnapshotDecreaseData) => void;
}

const getData = async (
  startDate: Date | null,
  endDate: Date | null,
  network?: string[],
  theme?: string[],
  planungsbereich?: string[],
  column?: string,
  minChange?: number,
  leadStartDate?: Date | null,
  leadEndDate?: Date | null,
): Promise<SnapshotDecreaseData[]> => {
  if (!startDate || !endDate) {
    throw new Error('No start date or end date provided');
  }

  const options: any = {
    start_date: dateToSqlDate(startDate),
    end_date: dateToSqlDate(endDate),
  };

  if (network && network.length > 0) {
    options.network = network;
  }
  if (theme && theme.length > 0) {
    options.theme = theme;
  }
  if (planungsbereich && planungsbereich.length > 0) {
    options.planungsbereich = planungsbereich;
  }
  if (column && column !== 'all') {
    options.column = column;
  }
  if (minChange !== undefined && minChange > 0) {
    options.min_change = minChange;
  }
  if (leadStartDate) {
    options.lead_start_date = dateToSqlDate(leadStartDate);
  }
  if (leadEndDate) {
    options.lead_end_date = dateToSqlDate(leadEndDate);
  }

  const decreasesData = (await fetchData<Record<string, any>[]>(
    '/api/vnr/snapshots/decreases',
    options,
  )) as Record<string, any>[];

  return decreasesData.map((item) => ({
    snapshot_date: item.snapshot_date,
    datum: item.datum,
    e_affiliate_id: item.e_affiliate_id,
    anmeldungen: parseInt(item.anmeldungen) || 0,
    column_name: item.column_name,
    old_value: item.old_value,
    new_value: item.new_value,
    change_amount: parseFloat(item.change_amount) || 0,
    change_percentage: parseFloat(item.change_percentage) || 0,
    network: item.network,
    theme: item.theme,
    planungsbereich: item.planungsbereich,
    newsletter: item.newsletter,
  }));
};

const getRowColor = (changeAmount: number): string => {
  const absChange = Math.abs(changeAmount);
  if (absChange >= 10) return 'rgba(244, 67, 54, 0.1)'; // Red for large decreases
  if (absChange >= 1) return 'rgba(255, 152, 0, 0.1)'; // Orange for medium decreases
  return 'transparent';
};

const truncateAffiliateId = (affiliateId: string): string => {
  if (affiliateId.length > 50) {
    return affiliateId.substring(0, 47) + '...';
  }
  return affiliateId;
};

export const SnapshotDecreasesTable: React.FC<SnapshotDecreasesTableProps> = ({
  startDate,
  endDate,
  network,
  theme,
  planungsbereich,
  column,
  minChange,
  leadStartDate,
  leadEndDate,
  onRowClick,
}) => {
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<DataKey>('change_amount');
  const [data, setData] = useState<SnapshotDecreaseData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!startDate || !endDate) {
      return;
    }

    setLoading(true);
    const fetchDataAsync = async () => {
      try {
        const data = await getData(
          startDate,
          endDate,
          network,
          theme,
          planungsbereich,
          column,
          minChange,
          leadStartDate,
          leadEndDate,
        );
        setData(data);
        setLoading(false);
      } catch (err: any) {
        setError(err);
        setLoading(false);
      }
    };

    fetchDataAsync();
  }, [
    startDate,
    endDate,
    network,
    theme,
    planungsbereich,
    column,
    minChange,
    leadStartDate,
    leadEndDate,
  ]);

  if (!startDate || !endDate) {
    return <div>Bitte wählen Sie einen Datumsbereich</div>;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  const headers = Object.keys(detailsHeaderMapping);

  const handleRequestSort = (property: keyof Data) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const createSortHandler = (property: keyof Data) => () => {
    handleRequestSort(property);
  };

  const sortedData = sortedRowInformation<SnapshotDecreaseData>(
    data,
    getComparator(order, orderBy),
  );

  return (
    <Box bgcolor="#fff">
      <Box sx={{ mb: 2 }}>
        <Button
          onClick={() => exportToExcel(sortedData, 'VNR-Snapshot-Wertminderungen')}
          sx={{ float: 'right' }}
        >
          Download als Excel
        </Button>
        <Box sx={{ clear: 'both', pt: 1 }}>
          <strong>Gefundene Wertminderungen:</strong> {data.length}
        </Box>
      </Box>
      <Box>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                {headers.map((header) => (
                  <TableCell key={header}>
                    <TableSortLabel
                      active={orderBy === header}
                      direction={orderBy === header ? order : 'asc'}
                      onClick={createSortHandler(header)}
                    >
                      {detailsHeaderMapping[header].name || header}
                      {orderBy === header ? (
                        <Box component="span" sx={{ ...visuallyHidden }}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map((row, index) => (
                <TableRow
                  key={index}
                  onClick={() => onRowClick && onRowClick(row)}
                  sx={{
                    backgroundColor: getRowColor(row.change_amount),
                    cursor: onRowClick ? 'pointer' : 'default',
                    '&:hover': {
                      backgroundColor: onRowClick
                        ? 'rgba(0, 0, 0, 0.08)'
                        : getRowColor(row.change_amount),
                    },
                  }}
                >
                  {Object.entries(detailsHeaderMapping).map(([key, value]) => (
                    <TableCell key={key}>
                      {key === 'e_affiliate_id' ? (
                        <span title={row[key] as string}>
                          {truncateAffiliateId(row[key] as string)}
                        </span>
                      ) : value.type === 'number' ? (
                        row[key] === null ? (
                          ''
                        ) : (
                          formatNumber(Number(row[key]), value.digits) + (value.attach || '')
                        )
                      ) : (
                        row[key]
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={headers.length} align="center">
                    Keine Wertminderungen gefunden
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default SnapshotDecreasesTable;
